import React, { useState, useEffect } from 'react';
import { Bell, BellOff, BellRing, Loader2, AlertCircle, CheckCircle2, Send, Clock, Calendar } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { usePushNotifications } from '@/hooks/usePushNotifications';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export function PushNotificationSettings({ user }) {
  const {
    isSupported,
    permission,
    isSubscribed,
    loading,
    error,
    subscribe,
    unsubscribe,
    sendTestNotification
  } = usePushNotifications(user);

  const [schedulerStatus, setSchedulerStatus] = useState(null);

  useEffect(() => {
    fetchSchedulerStatus();
  }, []);

  const fetchSchedulerStatus = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/push/scheduler-status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSchedulerStatus(response.data);
    } catch (err) {
      console.error('Error obteniendo estado del scheduler:', err);
    }
  };

  const handleSubscribe = async () => {
    const success = await subscribe();
    if (success) {
      toast.success('Notificaciones activadas correctamente');
    } else {
      toast.error(error || 'No se pudieron activar las notificaciones');
    }
  };

  const handleUnsubscribe = async () => {
    const success = await unsubscribe();
    if (success) {
      toast.success('Notificaciones desactivadas');
    } else {
      toast.error('Error al desactivar notificaciones');
    }
  };

  const handleTestNotification = async () => {
    try {
      await sendTestNotification();
      toast.success('Notificación de prueba enviada');
    } catch (err) {
      toast.error('Error al enviar notificación de prueba');
    }
  };

  if (!isSupported) {
    return (
      <Card className="border-yellow-200 bg-yellow-50 dark:bg-yellow-900/20">
        <CardContent className="p-4">
          <div className="flex items-center gap-3">
            <AlertCircle className="w-5 h-5 text-yellow-600" />
            <div>
              <p className="font-medium text-yellow-800 dark:text-yellow-200">
                Notificaciones no soportadas
              </p>
              <p className="text-sm text-yellow-700 dark:text-yellow-300">
                Tu navegador no soporta notificaciones push. Prueba con Chrome, Firefox o Edge.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (!user?.is_admin) {
    return (
      <Card className="border-gray-200">
        <CardContent className="p-4">
          <div className="flex items-center gap-3">
            <BellOff className="w-5 h-5 text-gray-400" />
            <div>
              <p className="font-medium text-gray-600">
                Notificaciones no disponibles
              </p>
              <p className="text-sm text-gray-500">
                Solo los administradores pueden recibir notificaciones de mantenimiento.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Bell className="w-5 h-5 text-emerald-600" />
            <CardTitle className="text-lg">Notificaciones Push</CardTitle>
          </div>
          {isSubscribed ? (
            <Badge className="bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-300">
              <CheckCircle2 className="w-3 h-3 mr-1" />
              Activas
            </Badge>
          ) : (
            <Badge variant="secondary">
              <BellOff className="w-3 h-3 mr-1" />
              Inactivas
            </Badge>
          )}
        </div>
        <CardDescription>
          Recibe alertas cuando se acerque el momento de realizar mantenimiento en las trampas.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Estado del permiso */}
        {permission === 'denied' && (
          <div className="p-3 bg-red-50 dark:bg-red-900/20 rounded-lg border border-red-200">
            <p className="text-sm text-red-700 dark:text-red-300">
              <AlertCircle className="w-4 h-4 inline mr-1" />
              Las notificaciones están bloqueadas. Por favor, habilítalas en la configuración del navegador.
            </p>
          </div>
        )}

        {/* Información de alertas */}
        <div className="grid grid-cols-2 gap-3">
          <div className="p-3 bg-amber-50 dark:bg-amber-900/20 rounded-lg">
            <p className="text-xs font-medium text-amber-700 dark:text-amber-300 mb-1">Aviso</p>
            <p className="text-sm text-amber-600 dark:text-amber-400">7 días antes del cambio</p>
          </div>
          <div className="p-3 bg-red-50 dark:bg-red-900/20 rounded-lg">
            <p className="text-xs font-medium text-red-700 dark:text-red-300 mb-1">Urgente</p>
            <p className="text-sm text-red-600 dark:text-red-400">3 días antes del cambio</p>
          </div>
        </div>

        {/* Tipos de alertas */}
        <div className="text-sm text-gray-600 dark:text-gray-400">
          <p className="font-medium mb-2">Recibirás alertas para:</p>
          <ul className="list-disc list-inside space-y-1">
            <li>Cambio de Feromonas (ciclo de 75 días)</li>
            <li>Cambio de Miel/Caldo (ciclo de 22 días)</li>
          </ul>
        </div>

        {/* Botones de acción */}
        <div className="flex flex-col sm:flex-row gap-2 pt-2">
          {isSubscribed ? (
            <>
              <Button
                variant="outline"
                onClick={handleUnsubscribe}
                disabled={loading}
                className="flex-1"
              >
                {loading ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <BellOff className="w-4 h-4 mr-2" />
                )}
                Desactivar
              </Button>
              <Button
                variant="secondary"
                onClick={handleTestNotification}
                disabled={loading}
                className="flex-1"
              >
                <Send className="w-4 h-4 mr-2" />
                Probar
              </Button>
            </>
          ) : (
            <Button
              onClick={handleSubscribe}
              disabled={loading || permission === 'denied'}
              className="w-full bg-emerald-600 hover:bg-emerald-700"
            >
              {loading ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <BellRing className="w-4 h-4 mr-2" />
              )}
              Activar Notificaciones
            </Button>
          )}
        </div>

        {error && (
          <p className="text-sm text-red-600 dark:text-red-400">
            <AlertCircle className="w-4 h-4 inline mr-1" />
            {error}
          </p>
        )}

        {/* Estado del verificador automático */}
        {schedulerStatus && (
          <div className="pt-3 border-t">
            <div className="flex items-center gap-2 mb-2">
              <Clock className="w-4 h-4 text-blue-600" />
              <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                Verificación Automática
              </span>
              {schedulerStatus.scheduler_running ? (
                <Badge className="bg-emerald-100 text-emerald-700 text-xs">Activo</Badge>
              ) : (
                <Badge variant="destructive" className="text-xs">Inactivo</Badge>
              )}
            </div>
            {schedulerStatus.jobs?.[0]?.next_run && (
              <p className="text-xs text-gray-500 dark:text-gray-400 flex items-center gap-1">
                <Calendar className="w-3 h-3" />
                Próxima verificación: {new Date(schedulerStatus.jobs[0].next_run).toLocaleString('es', {
                  dateStyle: 'short',
                  timeStyle: 'short'
                })}
              </p>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export default PushNotificationSettings;
